import { generateClient } from 'aws-amplify/api';
import { getCurrentUser } from 'aws-amplify/auth';
import * as mutations from '../graphql/mutations';
import * as queries from '../graphql/queries';

const client = generateClient();

// Domain key mapping to convert between numerical indices and text descriptions
export const getCSTDomainKeyByIndex = (index) => {
  const domainKeys = [
    "Operative Experience",
    "Surgical Experience",
    "Quality Improvement/Audit",
    "Presentations/Publications",
    "Teaching Experience"
  ];
  
  // Convert to number and ensure it's handled as zero-based index
  const numericIndex = Number(index);
  console.log(`Converting domain index ${index} (type: ${typeof index}) to key, numeric value: ${numericIndex}`);
  
  if (isNaN(numericIndex)) {
    console.error(`Invalid domain index: ${index} is not a number`);
    return `Unknown domain ${index}`;
  }
  
  // Use zero-based indexing (0-4)
  const result = domainKeys[numericIndex];
  console.log(`Domain lookup result for index ${numericIndex}: ${result}`);
  
  // Return the domain name or "Unknown domain" if index is out of range
  return result || `Unknown domain ${index}`;
};

// Helper function to determine if a domain key is numeric
const isNumericDomainKey = (key) => {
  const result = !isNaN(Number(key)) && key !== null && key !== '';
  console.log(`Checking if domain key "${key}" is numeric: ${result}`);
  return result;
};

/**
 * Saves or updates progress for a CST domain
 * @param {string|number} domainKey - The key identifying the domain/category (can be number or string)
 * @param {number} progress - The progress value to save (number of completed tasks)
 * @returns {Promise} - The GraphQL mutation result
 */
export const saveCSTDomainProgress = async (domainKey, progress) => {
  try {
    console.log(`Domain key before conversion: "${domainKey}" (type: ${typeof domainKey})`);
    
    // Convert numeric domainKey to text description
    const textDomainKey = isNumericDomainKey(domainKey) 
      ? getCSTDomainKeyByIndex(Number(domainKey))
      : domainKey;
    
    console.log(`Domain key after conversion: "${textDomainKey}"`);
    
    console.log(`Starting to save CST progress for domain ${textDomainKey}: ${progress}`);
    
    // Get current user
    const user = await getCurrentUser();
    if (!user) {
      console.error('Authentication error: User not authenticated');
      throw new Error('User not authenticated');
    }
    console.log('User authenticated:', user.username);

    // Check if there's already a record for this domain
    console.log(`Checking for existing record with userId: ${user.username}, domainKey: ${textDomainKey}`);
    const { data } = await client.graphql({
      query: queries.listCSTDomainProgresses,
      variables: {
        filter: { 
          userId: { eq: user.username }, 
          domainKey: { eq: textDomainKey }
        }
      },
      authMode: 'userPool'
    });
    console.log('Query result:', JSON.stringify(data));

    const existingRecord = data.listCSTDomainProgresses.items[0];

    if (existingRecord) {
      // Update existing record
      console.log(`Updating existing record ${existingRecord.id} with progress: ${progress}`);
      const input = {
        id: existingRecord.id,
        userId: user.username,
        domainKey: textDomainKey,
        progress,
        owner: user.username,
        _version: existingRecord._version // Include version if using DataStore
      };

      console.log('Update input:', JSON.stringify(input));
      const result = await client.graphql({
        query: mutations.updateCSTDomainProgress,
        variables: { input },
        authMode: 'userPool'
      });

      console.log(`Updated CST domain progress for ${textDomainKey}:`, result);
      return result;
    } else {
      // Create new record
      console.log(`Creating new record for userId: ${user.username}, domainKey: ${textDomainKey}, progress: ${progress}`);
      const input = {
        userId: user.username,
        domainKey: textDomainKey,
        progress,
        owner: user.username
      };

      console.log('Create input:', JSON.stringify(input));
      const result = await client.graphql({
        query: mutations.createCSTDomainProgress,
        variables: { input },
        authMode: 'userPool'
      });

      console.log(`Created CST domain progress for ${textDomainKey}:`, result);
      return result;
    }
  } catch (error) {
    console.error('Error saving CST domain progress:', error);
    console.error('Error details:', {
      message: error.message,
      name: error.name,
      stack: error.stack,
      errors: error.errors || 'No nested errors'
    });
    throw error;
  }
};

/**
 * Fetches progress for a specific CST domain
 * @param {string|number} domainKey - The key identifying the domain/category
 * @returns {Promise<Object>} - The domain progress object or null if not found
 */
export const fetchCSTDomainProgress = async (domainKey) => {
  try {
    console.log(`Fetching CST domain progress for domain key: "${domainKey}" (type: ${typeof domainKey})`);
    
    // Convert numeric domainKey to text description using the same logic as saveCSTDomainProgress
    const textDomainKey = isNumericDomainKey(domainKey) 
      ? getCSTDomainKeyByIndex(Number(domainKey))
      : domainKey;
    
    console.log(`Domain key after conversion for fetch: "${textDomainKey}"`);
      
    // Get current user
    const user = await getCurrentUser();
    if (!user) {
      console.error('Authentication error: User not authenticated during fetch');
      throw new Error('User not authenticated');
    }
    console.log(`Fetching for user: ${user.username}, domain: ${textDomainKey}`);

    const { data } = await client.graphql({
      query: queries.listCSTDomainProgresses,
      variables: {
        filter: { 
          userId: { eq: user.username }, 
          domainKey: { eq: textDomainKey }
        }
      },
      authMode: 'userPool'
    });

    console.log(`Fetch result for domain ${textDomainKey}:`, JSON.stringify(data.listCSTDomainProgresses.items));

    if (data.listCSTDomainProgresses.items.length > 0) {
      console.log(`Found existing progress for domain ${textDomainKey}:`, data.listCSTDomainProgresses.items[0].progress);
      return data.listCSTDomainProgresses.items[0];
    }
    
    console.log(`No progress found for domain ${textDomainKey}`);
    return null;
  } catch (error) {
    console.error(`Error fetching CST domain progress for ${domainKey}:`, error);
    console.error('Error details:', {
      message: error.message,
      name: error.name,
      stack: error.stack,
      errors: error.errors || 'No nested errors'
    });
    return null;
  }
};

/**
 * Fetches progress for all CST domains for the current user
 * @returns {Promise<Array>} - Array of domain progress objects
 */
export const fetchCSTDomainsProgress = async () => {
  try {
    console.log('Fetching progress for all CST domains');
    
    // Get current user
    const user = await getCurrentUser();
    if (!user) {
      console.error('Authentication error: User not authenticated during fetch all');
      throw new Error('User not authenticated');
    }
    console.log(`Fetching all domains for user: ${user.username}`);

    const { data } = await client.graphql({
      query: queries.listCSTDomainProgresses,
      variables: {
        filter: { userId: { eq: user.username } },
        limit: 100 // Ensure we get all records
      },
      authMode: 'userPool'
    });

    console.log('Fetched all CST domain progress items:', data.listCSTDomainProgresses.items.length);
    console.log('Items:', JSON.stringify(data.listCSTDomainProgresses.items));
    return data.listCSTDomainProgresses.items;
  } catch (error) {
    console.error('Error fetching CST domains progress:', error);
    console.error('Error details:', {
      message: error.message,
      name: error.name,
      stack: error.stack
    });
    return [];
  }
};
  
